import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { Placeholder } from '@tiptap/extensions';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { Box } from '@mantine/core';
import { useGetPuck } from '@puckeditor/core';
import { useBlockPropsWriter } from '../../../puck/blockPropsWriter';
import { registerPuckRichTextFlusher } from '../../../utils/puckRichTextSyncRegistry';
import { clearActiveRichTextEditor, setActiveRichTextEditor } from '../../../utils/activeRichTextEditor';
import { clearDroppedTextFocus, isDroppedTextFocusRequested } from '../../../utils/droppedTextFocus';
import { syncRichTextToPuck } from '../../../utils/syncRichTextToPuck';
import { usePuckEditableSurface } from '../../../hooks/usePuckEditableSurface';
import { useSavedEditorSelection } from '../../../hooks/useSavedEditorSelection';
import { stripTrailingEmptyParagraphs, toEditorHtml } from '../../../utils/richTextHtml';
import { webTextStyleToInlineCss } from '../../../utils/proposalTextStyle';
import { withTextBackgroundStyle } from '../../../utils/textBackgroundStyle';
import { FontFamily, FontSize } from '../../../tiptap/fontStyleExtensions';
import { ProposalVariableSuggestion } from '../../../tiptap/variableExtension';
import { BankLink } from '../../../tiptap/bankLinkExtension';
import { useStyledPaste } from '../../../tiptap/useStyledPaste';
import VariableSuggestionMenu from '../../../tiptap/VariableSuggestionMenu';
import RichTextBubbleTypographyControls from './RichTextBubbleTypographyControls';
import BubbleMenuShell from './BubbleMenuShell';
import RichTextBubbleColorControls from './RichTextBubbleColorControls';
import RichTextBubbleHighlightControls from './RichTextBubbleHighlightControls';
import RichTextBubbleLinkControls from './RichTextBubbleLinkControls';
import { BubbleAlignControls, BubbleFormatControls, BubbleListControls, BubbleVariableControl } from './BubbleFormatControls';
import {
  hasEditorSelection,
  shouldHideAfterBlur,
  shouldShowBubbleMenu as decideBubbleMenu,
} from './bubbleMenuVisibility';
import { createBubblePositioningHooks } from './bubbleMenuPositioning';
import { RICH_TEXT_LABELS } from './richTextLabels';
import styles from './ProposalRichTextBubbleEditor.module.css';

export const EXTENSIONS = [
  // trailingNode (StarterKit v3) ajoute un <p> vide quand le document ne finit
  // pas par un paragraphe, pour pouvoir cliquer dessous et écrire. DÉSACTIVÉ :
  // ce paragraphe se VOIT — une ligne vide apparaît sous une liste dès qu'on
  // clique dans le texte, elle occupe de la place pour rien, et elle partait
  // dans getHTML(). On sort d'une liste par Entrée sur une puce vide, et d'un
  // titre par Entrée en fin de ligne : rien n'est perdu.
  StarterKit.configure({ link: false, trailingNode: false }),
  // Lien Mantine + l'identifiant du lien de banque dont l'adresse a été copiée.
  BankLink,
  TextAlign.configure({ types: ['heading', 'paragraph', 'listItem'] }),
  TextStyle,
  FontFamily,
  FontSize,
  Color.configure({ types: ['textStyle'] }),
  // multicolor : le surlignage porte sa couleur (modèle Google Docs), au lieu
  // du jaune unique. Remplace les champs « couleur de fond du texte » de la
  // sidebar, qui ne pouvaient colorer que le bloc entier.
  Highlight.configure({ multicolor: true }),
  // Variables `@` : déclencheur unique pour tous les textes riches du canvas.
  ProposalVariableSuggestion,
];

/* shift : clampe le menu dans le viewport (sidebar étroite, bords d'écran).
   L'hôte du menu est le body du document de l'éditeur (cf. `appendTo` plus
   bas) : ses commandes vivent dans un portail React, elles gardent donc leurs
   gestionnaires — ce qui n'était pas vrai du temps de tiptap v2. */
const BUBBLE_MENU_OPTIONS = {
  placement: 'top', offset: 8, flip: true, shift: { padding: 8 },
};
/* Positionnement viewport : le menu s'affranchit du rognage d'un ancêtre
   `overflow`. La barre d'onglets défile en x et masque son débordement en y —
   elle coupait donc net un menu placé au-dessus du libellé. */
const BUBBLE_MENU_OPTIONS_FIXED = { ...BUBBLE_MENU_OPTIONS, strategy: 'fixed' };

/* Laisse aux commandes de la barre le temps de rendre le focus à l'éditeur
   (clic sur « Gras » : blur puis focus), et à une interaction de se conclure
   (150 ms côté BubbleMenuShell). */
const BLUR_HIDE_DELAY_MS = 250;

/* Barre flottante masquée pour l'instant : la section « Texte » de la sidebar
   porte la même mise en forme. Gardée pour pouvoir la rétablir. */
const SHOW_BUBBLE_MENU = false;

/** Position du texte sous un point de l'écran, sinon la fin du texte. */
function positionAt(editor, { x, y }) {
  try {
    return editor.view.posAtCoords({ left: x, top: y })?.pos ?? 'end';
  } catch {
    // Point hors du texte rendu (ou pas de mise en page, jsdom).
    return 'end';
  }
}

export default function ProposalRichTextBubbleEditor({
  id,
  html = '',
  contentProp = 'html',
  contentPath = null,
  textStyle = null,
  className = '',
  style,
  minHeight,
  drawMode = false,
  backgroundColor = '',
  // Toolbar réduite (ex. nom du menu) : masque liens, alignement et
  // « Ajouter à la palette » — inutiles pour un libellé court.
  reducedToolbar = false,
  // Texte affiché quand le contenu est vide (édition seulement, jamais rendu
  // en lecture) : garde une zone cliquable pour re-saisir.
  placeholder = '',
  // 'fixed' quand l'éditeur vit dans un conteneur qui rogne (overflow).
  bubbleStrategy,
  // Éditeur monté au clic (case de tableau) : `{ x, y }` du clic, ou 'end'.
  // Le clic a eu lieu avant que l'éditeur existe, il ne place donc rien.
  focusAt = null,
  // Demande servie : l'appelant l'oublie, un remontage ne la rejoue pas.
  onFocusApplied,
}) {
  const fieldKey = contentPath || contentProp;
  // Accesseur stable plutôt que trois abonnements : Puck recrée `dispatch`,
  // `getItemById` et `getSelectorForId` à chaque changement d'état. S'y abonner
  // faisait re-rendre **tous** les éditeurs de la page à chaque frappe — cent
  // cinq d'entre eux sur un tableau de cinq colonnes sur vingt lignes — et,
  // pire, recréait à chaque fois le `debounce` d'envoi en annulant le
  // précédent : une frappe en attente pouvait être perdue.
  const getPuck = useGetPuck();

  const isApplyingExternalHtml = useRef(false);
  const lastEmittedHtmlRef = useRef(html);
  const wrapRef = useRef(null);
  const contentRef = useRef(null);
  const bubbleMenuShellRef = useRef(null);
  const bubbleMenuActiveRef = useRef(false);
  const bubbleMenuLockRef = useRef(0);
  const [isStackElevated, setIsStackElevated] = useState(false);

  const writer = useBlockPropsWriter();

  const syncToPuck = useCallback((nextHtml) => {
    if (writer) {
      if (writer.write(id, fieldKey, nextHtml)) lastEmittedHtmlRef.current = nextHtml;
      return;
    }
    const { dispatch, getItemById, getSelectorForId, history, appState } = getPuck();
    const didSync = syncRichTextToPuck({
      id,
      contentProp,
      contentPath,
      nextHtml,
      history,
      appState,
      getItemById,
      getSelectorForId,
      dispatch,
    });
    if (didSync) {
      lastEmittedHtmlRef.current = nextHtml;
    }
  }, [contentPath, contentProp, fieldKey, getPuck, id, writer]);

  const debouncedSync = useMemo(() => debounce((html) => {
    syncToPuck(html);
  }, 200), [syncToPuck]);
  useEffect(() => () => debouncedSync.cancel(), [debouncedSync]);

  const editorRef = useRef(null);

  const styledPaste = useStyledPaste({ textStyle });
  const dismissPasteChoice = styledPaste.dismiss;

  const initialContentRef = useRef(null);
  const mountedIdRef = useRef(id);
  const mountedFieldRef = useRef(fieldKey);
  if (
    mountedIdRef.current !== id
    || mountedFieldRef.current !== fieldKey
    || initialContentRef.current === null
  ) {
    mountedIdRef.current = id;
    mountedFieldRef.current = fieldKey;
    initialContentRef.current = toEditorHtml(html);
    lastEmittedHtmlRef.current = html;
  }

  const extensions = useMemo(() => {
    if (!placeholder) return EXTENSIONS;
    return [...EXTENSIONS, Placeholder.configure({
      // Uniquement quand le texte est VRAIMENT vide. Sinon l'espace réservé
      // s'accroche au premier paragraphe vide venu — celui qui traîne à la fin
      // d'un contenu déjà enregistré, par exemple — et « Question » ou
      // « Réponse » s'affiche sous un texte qui n'a rien de vide.
      placeholder: ({ editor }) => (editor.isEmpty ? placeholder : ''),
    })];
  }, [placeholder]);

  const editor = useEditor({
    extensions,
    content: initialContentRef.current,
    shouldRerenderOnTransaction: false,
    editorProps: {
      handlePaste: styledPaste.handlePaste,
      handleKeyDown: styledPaste.handleKeyDown,
      handleDOMEvents: {
        keyup: styledPaste.handleKeyUp,
        blur: (view, event) => {
          const related = event.relatedTarget;
          if (!related || !(related instanceof Node)) return false;
          if (bubbleMenuShellRef.current?.contains(related)) return true;
          if (related instanceof Element) {
            if (related.closest('[data-portal]')) return true;
            if (related.closest('[class*="Popover-dropdown"]')) return true;
            if (related.closest('[role="dialog"]')) return true;
          }
          return false;
        },
      },
    },
    onUpdate: ({ editor: ed }) => {
      styledPaste.onEditorUpdate(ed);
      if (isApplyingExternalHtml.current) return;
      // Filet pour les contenus déjà enregistrés avec un paragraphe vide en
      // fin (ils datent d'avant la coupure de `trailingNode`) : ils se
      // nettoient à la première frappe au lieu de traîner leur ligne vide.
      debouncedSync(stripTrailingEmptyParagraphs(ed.getHTML()));
    },
  }, [id, fieldKey]);

  editorRef.current = editor;

  // Clé de plugin à nous : c'est par elle qu'on redemande un placement ou une
  // fermeture de la barre (`setMeta`).
  const bubbleMenuPluginKey = useMemo(() => `bubbleMenu-${id || 'x'}-${fieldKey}`, [id, fieldKey]);

  const hideBubbleMenu = useCallback(() => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    bubbleMenuActiveRef.current = false;
    ed.view.dispatch(ed.state.tr.setMeta(bubbleMenuPluginKey, 'hide'));
  }, [bubbleMenuPluginKey]);


  const flushRichTextToPuck = useCallback(() => {
    debouncedSync.flush();
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed || isApplyingExternalHtml.current) return;
    debouncedSync.cancel();
    syncToPuck(stripTrailingEmptyParagraphs(ed.getHTML()));
  }, [debouncedSync, syncToPuck]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    const onBlur = () => {
      debouncedSync.flush();
      dismissPasteChoice();
    };
    editor.on('blur', onBlur);
    return () => editor.off('blur', onBlur);
  }, [editor, debouncedSync, dismissPasteChoice]);

  /**
   * Refermer la barre quand l'éditeur perd le focus.
   *
   * `shouldShow` ne suffit pas : ProseMirror ne la consulte qu'au fil des
   * transactions, et perdre le focus n'en produit aucune. La fermeture de
   * tiptap ne suffit pas non plus — elle renonce dès que l'élément qui prend le
   * focus appartient au parent de la barre, et ce parent est le body.
   *
   * Le délai laisse le temps aux commandes de la barre de rendre le focus à
   * l'éditeur : cliquer « Gras » défocalise puis refocalise.
   */
  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    let timer = null;
    const cancel = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const onBlur = () => {
      cancel();
      timer = window.setTimeout(() => {
        timer = null;
        const ed = editorRef.current;
        if (!ed || ed.isDestroyed) return;
        const shell = bubbleMenuShellRef.current;
        const hide = shouldHideAfterBlur({
          isFocused: ed.isFocused,
          locks: bubbleMenuLockRef.current,
          focusInsideBar: !!shell?.contains(shell.ownerDocument?.activeElement),
        });
        if (hide) hideBubbleMenu();
      }, BLUR_HIDE_DELAY_MS);
    };
    editor.on('blur', onBlur);
    editor.on('focus', cancel);
    return () => {
      cancel();
      editor.off('blur', onBlur);
      editor.off('focus', cancel);
    };
  }, [editor, hideBubbleMenu]);

  useEffect(() => registerPuckRichTextFlusher(flushRichTextToPuck), [flushRichTextToPuck]);

  const { rememberNow, runWithSelection } = useSavedEditorSelection(editor);

  // Curseur dans le texte : la sidebar montre sa mise en forme (section
  // « Texte », cf. RichTextSidebarSection).
  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    const activate = () => setActiveRichTextEditor({ id, editor, reducedToolbar });
    editor.on('focus', activate);
    return () => {
      editor.off('focus', activate);
      clearActiveRichTextEditor(editor);
    };
  }, [editor, id, reducedToolbar]);

  // Texte tout juste déposé : on y écrit sans cliquer dedans. Une frame
  // d'attente, le temps que Puck termine le dépôt.
  useEffect(() => {
    if (!editor || editor.isDestroyed || !isDroppedTextFocusRequested(id)) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (!editor.isDestroyed) editor.commands.focus('all');
    });
    const clearRequest = () => clearDroppedTextFocus(id);
    editor.on('update', clearRequest);
    return () => {
      window.cancelAnimationFrame(frame);
      editor.off('update', clearRequest);
    };
  }, [editor, id]);

  useEffect(() => {
    if (!editor || editor.isDestroyed || !focusAt) return;
    editor.commands.focus(typeof focusAt === 'object' ? positionAt(editor, focusAt) : 'end');
    onFocusApplied?.();
    // Une fois, au montage : la demande décrit le clic qui a réveillé la case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  usePuckEditableSurface(contentRef, editor, !!editor, id);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    const elevate = () => setIsStackElevated(true);
    const lower = () => {
      window.setTimeout(() => {
        if (!editor.isDestroyed && !editor.isFocused && !bubbleMenuActiveRef.current) {
          setIsStackElevated(false);
        }
      }, 0);
    };
    editor.on('focus', elevate);
    editor.on('blur', lower);
    return () => {
      editor.off('focus', elevate);
      editor.off('blur', lower);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (editor.isFocused) return;
    if (html === lastEmittedHtmlRef.current) return;
    const next = toEditorHtml(html);
    // Comparaison sur la même base que ce qu'on émet, sinon un contenu encore
    // porteur d'un paragraphe vide de fin se ferait réécrire à chaque passage.
    if (stripTrailingEmptyParagraphs(editor.getHTML()) === stripTrailingEmptyParagraphs(next)) {
      lastEmittedHtmlRef.current = html;
      return;
    }
    debouncedSync.cancel();
    const { from, to } = editor.state.selection;
    isApplyingExternalHtml.current = true;
    editor.commands.setContent(next, { emitUpdate: false });
    const size = editor.state.doc.content.size;
    if (from <= size) {
      editor.commands.setTextSelection({
        from: Math.min(from, size),
        to: Math.min(to, size),
      });
    }
    isApplyingExternalHtml.current = false;
    lastEmittedHtmlRef.current = html;
  }, [html, editor, debouncedSync]);

  const lockBubbleMenu = useCallback(() => {
    bubbleMenuLockRef.current += 1;
    bubbleMenuActiveRef.current = true;
  }, []);

  const unlockBubbleMenu = useCallback(() => {
    bubbleMenuLockRef.current = Math.max(0, bubbleMenuLockRef.current - 1);
    if (bubbleMenuLockRef.current > 0) return;
    // Sans ça, activeRef restait bloqué à true après une interaction popover
    // (onInteractionEnd sort tôt quand le menu est locké) : le bubble menu
    // réapparaissait ensuite au simple clic curseur, sans sélection.
    window.setTimeout(() => {
      if (bubbleMenuLockRef.current > 0) return;
      bubbleMenuActiveRef.current = false;
    }, 200);
  }, []);

  const restoreBubbleSelection = useCallback(() => {
    runWithSelection((chain) => chain.focus());
    bubbleMenuActiveRef.current = true;
  }, [runWithSelection]);

  /**
   * Tiptap BubbleMenu ne met preventHide qu'au mousedown (capture).
   * Mantine NumberInput step sur pointerdown puis focus l'input → blur éditeur
   * → hide() retire le menu du DOM avant l'incrément.
   */
  const handleBubbleMenuPointerDownCapture = useCallback((e) => {
    bubbleMenuActiveRef.current = true;
    setIsStackElevated(true);
    rememberNow();
    e.currentTarget.dispatchEvent(
      new MouseEvent('mousedown', { bubbles: true, cancelable: true }),
    );
  }, [rememberNow]);

  /**
   * Ce que la barre montre dépend d'abord de la sélection : ProseMirror ne la
   * vide pas au blur, et c'est sur son rectangle que la barre se place. La
   * règle vit dans `bubbleMenuVisibility`, où elle se teste.
   */
  // Tiptap montre la barre, puis calcule sa position de façon asynchrone — et
  // abandonne ce calcul si la barre n'est pas encore rattachée quand il
  // retombe. Résultat : à la première sélection d'une cellule, elle restait en
  // haut du cadre. On lui redemande son placement jusqu'à l'obtenir, barre
  // effacée en attendant. La clé de plugin doit être à nous pour pouvoir le
  // redemander.

  /**
   * Redemande le placement de la barre. Si elle s'est retrouvée détachée du
   * document — ce qui arrive quand React refait le sous-arbre de la cellule au
   * moment où le bloc est sélectionné —, tiptap ne la replace jamais tout seul :
   * son `show()` sort tout de suite parce qu'il se croit déjà visible, et son
   * calcul de position abandonne sur un élément non rattaché. On le force alors
   * à refermer puis rouvrir, ce qui la rattache.
   */
  const requestBubblePlacement = useCallback(() => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    const shell = bubbleMenuShellRef.current;
    if (shell && !shell.isConnected) {
      ed.view.dispatch(ed.state.tr.setMeta(bubbleMenuPluginKey, 'hide'));
      ed.view.dispatch(ed.state.tr.setMeta(bubbleMenuPluginKey, 'show'));
      return;
    }
    ed.view.dispatch(ed.state.tr.setMeta(bubbleMenuPluginKey, 'updatePosition'));
  }, [bubbleMenuPluginKey]);

  const positioning = useMemo(
    () => createBubblePositioningHooks(
      () => bubbleMenuShellRef.current,
      { requestPlacement: requestBubblePlacement },
    ),
    [requestBubblePlacement],
  );
  useEffect(() => positioning.dispose, [positioning]);

  const getBubbleMenuHost = useCallback(() => {
    const ed = editorRef.current;
    return ed?.view?.dom?.ownerDocument?.body || document.body;
  }, []);

  const bubbleMenuOptions = useMemo(() => ({
    ...(bubbleStrategy === 'fixed' ? BUBBLE_MENU_OPTIONS_FIXED : BUBBLE_MENU_OPTIONS),
    onShow: positioning.onShow,
    onHide: positioning.onHide,
    onUpdate: positioning.onUpdate,
  }), [bubbleStrategy, positioning]);

  const shouldShowBubbleMenu = useCallback(({ state }) => decideBubbleMenu({
    hasSelection: hasEditorSelection(state.selection),
    isFocused: !!editorRef.current?.isFocused,
    interacting: bubbleMenuActiveRef.current,
    locks: bubbleMenuLockRef.current,
  }), []);

  const boxStyle = withTextBackgroundStyle(
    {
      ...(minHeight ? { minHeight } : {}),
      ...(style || {}),
      ...webTextStyleToInlineCss(textStyle, { drawMode }),
    },
    backgroundColor || textStyle?.backgroundColor,
  );

  if (!editor) return null;

  return (
    <Box
      ref={wrapRef}
      className={`${styles.wrap} ${isStackElevated ? styles.wrapElevated : ''} ${className}`.trim()}
      style={boxStyle}
      data-richtext-elevated={isStackElevated ? 'true' : undefined}
      onMouseDown={(e) => {
        // Clic dans le texte : ProseMirror place le caret lui-même — un
        // focus() programmatique ici l'écrasait (caret pas sous le clic au
        // premier clic). On ne force le focus que sur le padding autour.
        if (e.target instanceof Element && e.target.closest('.ProseMirror')) return;
        editor.commands.focus();
      }}
    >
      <RichTextEditor
        editor={editor}
        variant="subtle"
        classNames={{ root: styles.editorRoot, content: styles.editorContent }}
        labels={RICH_TEXT_LABELS}
      >
        {editor && SHOW_BUBBLE_MENU && (
          <BubbleMenu
            editor={editor}
            pluginKey={bubbleMenuPluginKey}
            /* La barre est accrochée au body du document de l'éditeur (celui de
               l'iframe du canvas), pas au parent du texte : accrochée dans la
               cellule, elle disparaissait du document dès que React refaisait ce
               sous-arbre — à la sélection du bloc, donc au premier clic — et
               tiptap ne la replaçait plus jamais. Ses commandes, elles, restent
               dans l'arbre React (portail), contrairement à l'appendTo de
               tiptap v2 qui les détachait vraiment. */
            appendTo={getBubbleMenuHost}
            options={bubbleMenuOptions}
            style={{ zIndex: 300 }}
            shouldShow={shouldShowBubbleMenu}
            onPointerDownCapture={handleBubbleMenuPointerDownCapture}
            data-puck-overlay-portal="true"
          >
            <BubbleMenuShell
              shellRef={bubbleMenuShellRef}
              rememberSelection={rememberNow}
              onInteractionStart={() => {
                bubbleMenuActiveRef.current = true;
                setIsStackElevated(true);
              }}
              onInteractionEnd={() => {
                if (bubbleMenuLockRef.current > 0) return;
                window.setTimeout(() => {
                  if (bubbleMenuLockRef.current > 0) return;
                  bubbleMenuActiveRef.current = false;
                  if (!editor.isDestroyed && !editor.isFocused && !bubbleMenuActiveRef.current) {
                    setIsStackElevated(false);
                  }
                }, 150);
              }}
            >
              <div className={styles.bubbleMenuToolbar}>
                <div className={styles.bubbleColorButtons}>
                  <RichTextBubbleColorControls
                    editor={editor}
                    rememberNow={rememberNow}
                    runWithSelection={runWithSelection}
                    onRestoreSelection={restoreBubbleSelection}
                    lockBubbleMenu={lockBubbleMenu}
                    unlockBubbleMenu={unlockBubbleMenu}
                  />
                  <RichTextBubbleHighlightControls
                    editor={editor}
                    rememberNow={rememberNow}
                    runWithSelection={runWithSelection}
                    onRestoreSelection={restoreBubbleSelection}
                    lockBubbleMenu={lockBubbleMenu}
                    unlockBubbleMenu={unlockBubbleMenu}
                  />
                </div>
                <BubbleFormatControls
                  editor={editor}
                  rememberNow={rememberNow}
                  runWithSelection={runWithSelection}
                />
                <BubbleVariableControl
                  editor={editor}
                  rememberNow={rememberNow}
                  runWithSelection={runWithSelection}
                />
                {!reducedToolbar && (
                  <RichTextBubbleLinkControls
                    editor={editor}
                    rememberNow={rememberNow}
                    runWithSelection={runWithSelection}
                    lockBubbleMenu={lockBubbleMenu}
                    unlockBubbleMenu={unlockBubbleMenu}
                    onRestoreSelection={restoreBubbleSelection}
                  />
                )}
                <RichTextBubbleTypographyControls
                  editor={editor}
                  rememberNow={rememberNow}
                  runWithSelection={runWithSelection}
                  lockBubbleMenu={lockBubbleMenu}
                  unlockBubbleMenu={unlockBubbleMenu}
                  onRestoreSelection={restoreBubbleSelection}
                />
                {!reducedToolbar && (
                  <>
                    <BubbleAlignControls
                      editor={editor}
                      rememberNow={rememberNow}
                      runWithSelection={runWithSelection}
                    />
                    <BubbleListControls
                      editor={editor}
                      rememberNow={rememberNow}
                      runWithSelection={runWithSelection}
                    />
                  </>
                )}
              </div>
            </BubbleMenuShell>
          </BubbleMenu>
        )}
        <div ref={contentRef} className={styles.contentSurface}>
          <RichTextEditor.Content className={styles.content} />
        </div>
        {/* Rendu par le seul éditeur dont le menu `@` est ouvert : le registre
            renvoie `null` (référence stable) à tous les autres. */}
        <VariableSuggestionMenu editor={editor} />
      </RichTextEditor>
      {styledPaste.pasteChoice}
    </Box>
  );
}
